/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/**
* Transform stream.
*
* @module @stdlib/streams-node-transform
*
* @example
* import stdout from '@stdlib/streams-node-stdout';
* import transformStream from '@stdlib/streams-node-transform';
*
* function transform( chunk, enc, clbk ) {
*     clbk( null, chunk.toString()+'\n' );
* }
*
* var opts = {
*     'transform': transform
* };
* var stream = transformStream( opts );
*
* stream.pipe( stdout );
*
* stream.write( '1' );
* stream.write( '2' );
* stream.write( '3' );
*
* stream.end();
* // e.g., => '1\n2\n3\n'
*
* @example
* import transformStream from '@stdlib/streams-node-transform';
*
* function transform( chunk, enc, clbk ) {
*     clbk( null, chunk.toString()+'\n' );
* }
*
* var opts = {
*     'objectMode': true,
*     'encoding': 'utf8',
*     'highWaterMark': 64,
*     'decodeStrings': false
* };
*
* var factory = transformStream.factory( opts );
*
* // Create 10 identically configured streams...
* var streams = [];
* var i;
* for ( i = 0; i < 10; i++ ) {
*     streams.push( factory( transform ) );
* }
*
* @example
* import stdout from '@stdlib/streams-node-stdout';
* import transformStream from '@stdlib/streams-node-transform';
*
* function stringify( chunk, enc, clbk ) {
*     clbk( null, JSON.stringify( chunk ) );
* }
*
* function newline( chunk, enc, clbk ) {
*     clbk( null, chunk+'\n' );
* }
*
* var s1 = transformStream.objectMode({
*     'transform': stringify
* });
*
* var s2 = transformStream.objectMode({
*     'transform': newline
* });
*
* s1.pipe( s2 ).pipe( stdout );
*
* s1.write( {'value': 'a'} );
* s1.write( {'value': 'b'} );
* s1.write( {'value': 'c'} );
*
* s1.end();
* // e.g., => '{"value":"a"}\n{"value":"b"}\n{"value":"c"}\n'
*
* @example
* import stdout from '@stdlib/streams-node-stdout';
* import transformStream from '@stdlib/streams-node-transform';
*
* function transform( chunk, enc, clbk ) {
*     clbk( null, chunk.toString()+'\n' );
* }
*
* var opts = {
*     'transform': transform
* };
*
* var Stream = transformStream.ctor( opts );
*
* var stream = new Stream();
*
* stream.pipe( stdout );
*
* stream.write( '1' );
* stream.write( '2' );
* stream.write( '3' );
*
* stream.end();
* // e.g., => '1\n2\n3\n'
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import main from './main.js';
import objectMode from './object_mode.js';
import factory from './factory.js';
import ctor from './ctor.js';


// MAIN //

setReadOnly( main, 'objectMode', objectMode );
setReadOnly( main, 'factory', factory );
setReadOnly( main, 'ctor', ctor );


// EXPORTS //

export default main;
